/* eslint-disable max-lines */
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyFootNote,
  TypographyP2,
} from '@heidi/design-system/typography';
import { ListChecks, MoreHorizontal, Trash2 } from 'lucide-react';

import { clinicalTasksPendingOpenTaskIdAtom } from '@/atoms/clinicalTasksAtoms';
import { useHcpVerificationStatus } from '@/features/settings/account/components/hcp-verification/hcp-verification-state';
import { useOpenHcpVerificationModal } from '@/features/settings/account/components/hcp-verification/use-open-hcp-verification-modal';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import {
  useMutateDeleteNotification,
  useMutateReadNotification,
  useMutateUnreadNotification,
} from '@/hooks/mutation/useMutateUpdateNotifications';
import { GET_CLINICAL_TASKS } from '@/hooks/query/useQueryClinicalTasks';
import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import { cn } from '@/lib/cn';
import { isTauri } from '@/tauri/utils';
import { NotificationType } from '@/types';
import { formatRelativeTimeFromNow } from '@/utils/dates';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { NotificationGroup, NotificationStatus } from '@heidi/schema/ml-scribe';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotificationsPopoverControl } from './notifications-popover-focus';
import { useShareNotificationTracking } from './use-share-notification-tracking';
import { useTaskNotificationTracking } from './use-task-notification-tracking';

// Extracts a named query param from a URL that may be absolute or relative.
// Uses the URL API for absolute URLs (handles encoding correctly); falls back
// to URLSearchParams on the raw query string for relative paths like '/tasks?task_id=...'.
const extractSearchParam = (
  href: string | null | undefined,
  param: string
): string | null => {
  if (!href) return null;
  try {
    return new URL(href).searchParams.get(param);
  } catch {
    return new URLSearchParams(href.split('?')[1] ?? '').get(param);
  }
};

const NotificationCard = ({ index }: { index: number }) => {
  const { data: notifications } = useQueryNotifications();
  const {
    title,
    status,
    id,
    created_at,
    notification_group,
    notification_name,
    action_link,
  } = notifications?.at(index) ?? {};

  const { mutate: mutateReadNotification } = useMutateReadNotification();
  const { mutate: mutateDeleteNotification } = useMutateDeleteNotification();
  const { mutate: mutateUnreadNotification } = useMutateUnreadNotification();

  const router = useRouter();
  const queryClient = useQueryClient();
  const setPendingOpenTaskId = useSetAtom(clinicalTasksPendingOpenTaskIdAtom);
  const openHcpVerificationModal = useOpenHcpVerificationModal();
  const { state: hcpVerificationState } = useHcpVerificationStatus();
  const { open: openSettingsModal } = useSettingsModal();
  const { skipFocusReturn: skipNotificationsPopoverFocusReturn, close: closePopover } =
    useNotificationsPopoverControl();

  const intl = useIntl();

  const { trackClicked: trackShareNotificationClicked } = useShareNotificationTracking(
    notification_name === NotificationType.SESSION_SHARED,
    id,
    action_link?.match(/\/session\/([^/?#]+)/)?.[1]
  );

  const taskNotificationType =
    notification_name === NotificationType.TASK_ASSIGNED ? 'assigned' : undefined;
  const { trackClicked: trackTaskNotificationClicked } = useTaskNotificationTracking(
    notification_group === NotificationGroup.TASK,
    action_link ? (extractSearchParam(action_link, 'task_id') ?? undefined) : undefined,
    taskNotificationType
  );

  const formatActionLink = (actionLink: string) => {
    if (isTauri()) {
      try {
        const url = new URL(actionLink);
        return url.pathname + url.search + url.hash;
      } catch {
        return actionLink;
      }
    }

    const isE2E =
      typeof globalThis !== 'undefined' && globalThis.window?.__IS_E2E__ === true;
    const isLocalhost =
      typeof globalThis !== 'undefined' &&
      globalThis.window?.location?.hostname === 'localhost';

    if (isE2E || isLocalhost) {
      const url = new URL(actionLink);
      url.protocol = 'http:';
      url.host = 'localhost:3000';
      return url.toString();
    }
    return actionLink;
  };

  // Performs the click action for a notification group (router push,
  // settings modal open, or sidebar tab swap) and returns true when it
  // actually navigated, so the caller can decide whether to dismiss the
  // notifications surface. Returning false keeps the popover open — e.g.
  // `GENERAL` has no destination, and the Tauri invite error path bails
  // before navigating.
  const performNotificationGroupAction = (group?: NotificationGroup): boolean => {
    // The daily clinician-verification reminder is a GENERAL inmail whose
    // click opens the NPI dialog in place — the dialog is already mounted
    // with the universal modals, so there is no settings hop. Only the
    // states that can still submit: a leftover row after verify, park, or
    // a loading first paint must stay a plain GENERAL no-op, same as
    // ineligible. The one-shot "verified" inmail has no branch.
    if (
      notification_name === NotificationType.HCP_VERIFICATION_NEEDED &&
      (hcpVerificationState === 'unverified' || hcpVerificationState === 'rejected')
    ) {
      openHcpVerificationModal();
      return true;
    }
    if (!group) {
      return false;
    }
    switch (group) {
      case NotificationGroup.BILLING:
        openSettingsModal('/settings/billing', 'notification_card');
        return true;
      case NotificationGroup.TASK: {
        // Carry task_id via atom rather than a URL query param so it never
        // reaches Snowplow analytics (trackPageView fires on routeChangeComplete
        // using document.URL verbatim before any shallow router.replace runs).
        // We always navigate to the relative path '/tasks' — not action_link
        // directly — so formatActionLink is not needed here.
        //
        // Invalidate the tasks cache before navigating so that on arrival the
        // query immediately starts a fresh fetch. Without this, a cached list
        // (isLoading=false, isFetching=true) could briefly show stale data
        // and useOpenTaskFromQueryParam would miss the newly-assigned task.
        trackTaskNotificationClicked();
        void queryClient.invalidateQueries({ queryKey: [GET_CLINICAL_TASKS] });
        const taskId = extractSearchParam(action_link, 'task_id');
        if (taskId) {
          setPendingOpenTaskId(taskId);
        }
        void router.push('/tasks');
        return true;
      }
      case NotificationGroup.GENERAL:
        return false;
      case NotificationGroup.SESSION:
        if (action_link) {
          void router.push(action_link);
        } else {
          // No specific session target — land on the sessions list. Under
          // the new-session-list flag this is the destination; under the
          // legacy flag the `[[...id]].tsx` route replaces to `/scribe/session`
          // which resolves to the user's most recent session.
          void router.push('/scribe/sessions');
        }
        return true;
      case NotificationGroup.TEAM:
        if (notification_name === NotificationType.NEW_INVITATION && action_link) {
          const actionLink = formatActionLink(action_link);
          if (isTauri()) {
            try {
              void router.push(actionLink);
              return true;
            } catch (error) {
              toast({
                tone: 'destructive',
                title: intl.formatMessage({
                  id: 'IN8rCWQvCA',
                  defaultMessage: 'Invalid invitation link',
                }),
                description: isHeidiSupportReachable
                  ? intl.formatMessage({
                      id: '08JjzSrj4N',
                      defaultMessage:
                        'The invitation link could not be processed. Please try again or contact support.',
                    })
                  : intl.formatMessage({
                      id: '/9YcSMwXKb',
                      defaultMessage:
                        'The invitation link could not be processed. Please try again or contact your IT team.',
                    }),
              });
              logger.error(
                `Failed to parse joining team invitation link: ${actionLink}`,
                error
              );
              return false;
            }
          }
          void router.push(actionLink);
          return true;
        }
        void router.push('/team');
        return true;
      case NotificationGroup.TEMPLATE:
        if (action_link) {
          void router.push(action_link);
        } else {
          void router.push('/scribe/my-templates');
        }
        return true;
      default:
        return false;
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger
        onClick={(e) => {
          e.stopPropagation();
          if (status === NotificationStatus.UNREAD) {
            mutateReadNotification(id ?? '');
          }
          trackShareNotificationClicked();
          const navigated = performNotificationGroupAction(notification_group);
          // Only dismiss the popover when we actually routed somewhere —
          // a click on a `GENERAL` row (or any no-op branch) should leave
          // it open so the user stays in context.
          if (navigated) {
            // Tell the popover (no-op outside it) to skip Radix's focus
            // restore — otherwise the bell re-focuses mid-navigation and
            // pops its tooltip on the destination page.
            skipNotificationsPopoverFocusReturn();
            closePopover();
          }
        }}
        // Row-level aria-label rolls the unread state into the title's
        // announcement so screen readers say "{title}, unread" once per row
        // instead of "unread" being announced as its own utterance via the
        // dot. The dot becomes purely decorative (aria-hidden below).
        aria-label={
          !title
            ? undefined
            : status === NotificationStatus.UNREAD
              ? intl.formatMessage(
                  {
                    defaultMessage: '{title}, unread',
                    id: 'mDdxlFxfpI',
                  },
                  { title }
                )
              : title
        }
        className={cn(
          'group mb-0.5 flex w-full cursor-pointer flex-col pb-2 pt-3 transition ease-in-out hover:bg-surface-hover',
          // Popover lives in a rounded floating container, so cards get
          // matching rounding + tighter inline padding.
          'rounded-lg px-3.5'
        )}
      >
        <div className="flex flex-col gap-0.5">
          {/* `items-start` keeps the unread dot pinned to the top of
              wrapped titles so it sits next to the first line. */}
          <div className="flex items-start justify-between gap-2">
            {status === NotificationStatus.READ ? (
              <TypographyCaption className="text-text-secondary">
                {title}
              </TypographyCaption>
            ) : (
              <TypographyCaptionBold className="start-0.5">{title}</TypographyCaptionBold>
            )}
            {status === NotificationStatus.UNREAD && (
              <span
                aria-hidden="true"
                className="me-0.5 mt-1.5 size-1.5 shrink-0 rounded-full bg-validation-destructive"
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <TypographyFootNote className="text-text-secondary">
              {formatRelativeTimeFromNow(created_at, intl.locale)}
            </TypographyFootNote>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="panel_ghost"
                  size="xxs"
                  iconOnly
                  flush="end"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Notification actions',
                    id: 'fVQD3wh+RY',
                  })}
                  onClick={(e) => e.stopPropagation()}
                  // Action reveal (mouse/trackpad only via `pointer-fine:`):
                  // hidden until row hover, kebab focus-visible, or open menu.
                  // `focus-visible` (not `focus-within`) so the kebab fades
                  // out when Radix returns focus to the trigger after a
                  // mouse-driven menu close.
                  className={cn(
                    'transition-opacity duration-200 ease-out',
                    'pointer-fine:opacity-0',
                    'pointer-fine:group-hover:opacity-100',
                    'pointer-fine:focus-visible:opacity-100',
                    'pointer-fine:data-[state=open]:opacity-100'
                  )}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-48"
                side="bottom"
                align="end"
                alignOffset={-12.5}
              >
                <DropdownMenuItem
                  className="flex !cursor-pointer justify-start gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (status === NotificationStatus.READ) {
                      mutateUnreadNotification(id ?? '');
                    } else {
                      mutateReadNotification(id ?? '');
                    }
                  }}
                >
                  <ListChecks size={14} />
                  <TypographyP2>
                    {status === NotificationStatus.READ ? (
                      <FormattedMessage defaultMessage="Mark as unread" id="vLAPJDu9y6" />
                    ) : (
                      <FormattedMessage defaultMessage="Mark as read" id="64Hq/+q+KI" />
                    )}
                  </TypographyP2>
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  className="flex !cursor-pointer justify-start gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    mutateDeleteNotification(id ?? '');
                  }}
                >
                  <Trash2 size={14} />
                  <TypographyP2>
                    <FormattedMessage defaultMessage="Delete" id="K3r6DQW7h+" />
                  </TypographyP2>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-48">
        <ContextMenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (status === NotificationStatus.READ) {
              mutateUnreadNotification(id ?? '');
            } else {
              mutateReadNotification(id ?? '');
            }
          }}
        >
          <ListChecks />
          <TypographyP2>
            {status === NotificationStatus.READ ? (
              <FormattedMessage defaultMessage="Mark as unread" id="vLAPJDu9y6" />
            ) : (
              <FormattedMessage defaultMessage="Mark as read" id="64Hq/+q+KI" />
            )}
          </TypographyP2>
        </ContextMenuItem>
        <ContextMenuItem
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            mutateDeleteNotification(id ?? '');
          }}
        >
          <Trash2 />
          <TypographyP2>
            <FormattedMessage defaultMessage="Delete" id="K3r6DQW7h+" />
          </TypographyP2>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default NotificationCard;
